import { useEffect, useRef, useState } from "react";

// Fontaine M26 — debounced auto-save with status (Saving… → Saved ✓ → idle; error + retry). Used by Field;
// use it directly when a control is not inside a Field.

/**
 * Hook: debounced persistence with status. Use it when a control is not inside a Field.
 * @param {(value) => Promise<any>} save
 * @param {{ debounce?: number, retries?: number }} [opts]
 * @returns {{ status: "idle"|"saving"|"saved"|"error", error: string|null, commit: (value) => void, flush: () => Promise<void>, retry: () => void }}
 */
export default function useAutosave(save, { debounce = 600, retries = 1 } = {}) {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const pending = useRef(undefined);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; clearTimeout(timer.current); }, []);

  const run = async (value, attempt = 0) => {
    setStatus("saving");
    setError(null);
    try {
      await save(value);
      if (!alive.current) return;
      pending.current = undefined;
      setStatus("saved");
      clearTimeout(timer.current);
      timer.current = setTimeout(() => alive.current && setStatus((s) => (s === "saved" ? "idle" : s)), 2000);
    } catch (e) {
      if (!alive.current) return;
      if (attempt < retries) { run(value, attempt + 1); return; }
      pending.current = value;
      setError(e?.message || "Couldn't save — try again");
      setStatus("error");
    }
  };
  const commit = (value) => {
    pending.current = value;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(value), debounce);
  };
  const flush = async () => { clearTimeout(timer.current); if (pending.current !== undefined) await run(pending.current); };
  const retry = () => { if (pending.current !== undefined) run(pending.current); };
  return { status, error, commit, flush, retry };
}

